import {
	ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
	AthenaEventIngressError,
} from "../../../../../runtime/ingress/errors.ts";
import { MOLLIE_WEBHOOK_MAX_BODY_BYTES } from "./verify-webhook.ts";

export const MOLLIE_WEBHOOK_MAX_AGE_MS = 15 * 60 * 1000;

export type MollieWebhookEnvelope =
	| { kind: "classic"; id: string }
	| {
		kind: "next_gen";
		id: string;
		type: string;
		resource: string;
		entityId: string;
		createdAt: string;
	};

export type MollieWebhookBodyKind = "empty" | "json" | "form" | "unknown";

export interface MollieWebhookEnvelopeDiagnostics {
	bodyBytes: number;
	bodyKind: MollieWebhookBodyKind;
	contentType?: string;
}

function decodeBody(body: Uint8Array): string {
	return new TextDecoder().decode(body);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return value != null && typeof value === "object" && !Array.isArray(value);
}

export function classifyMollieWebhookBody(
	body: Uint8Array,
	contentType?: string,
): MollieWebhookEnvelopeDiagnostics {
	const text = decodeBody(body).trim();
	let bodyKind: MollieWebhookBodyKind = "unknown";
	if (text.length === 0) {
		bodyKind = "empty";
	} else if (text.startsWith("{") || text.startsWith("[")) {
		bodyKind = "json";
	} else if (text.includes("=")) {
		bodyKind = "form";
	}
	return {
		bodyBytes: body.byteLength,
		bodyKind,
		...(contentType ? { contentType } : {}),
	};
}

function envelopeError(
	message: string,
	diagnostics: MollieWebhookEnvelopeDiagnostics,
): AthenaEventIngressError {
	return new AthenaEventIngressError({
		code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
		diagnostics,
		domain: "billing",
		message,
		provider: "mollie",
		retryable: false,
	});
}

function assertMollieWebhookBodySize(
	body: Uint8Array,
	diagnostics: MollieWebhookEnvelopeDiagnostics,
): void {
	if (body.byteLength > MOLLIE_WEBHOOK_MAX_BODY_BYTES) {
		throw envelopeError("Mollie webhook payload exceeds size limit.", diagnostics);
	}
}

function assertMollieWebhookCreatedAt(
	createdAt: string | undefined,
	diagnostics: MollieWebhookEnvelopeDiagnostics,
): asserts createdAt is string {
	if (createdAt == null || createdAt.trim() === "") {
		throw envelopeError("Mollie webhook createdAt is required.", diagnostics);
	}
	const created = Date.parse(createdAt);
	if (!Number.isFinite(created)) {
		throw envelopeError("Mollie webhook createdAt is invalid.", diagnostics);
	}
	if (Math.abs(Date.now() - created) > MOLLIE_WEBHOOK_MAX_AGE_MS) {
		throw envelopeError("Mollie webhook createdAt is stale.", diagnostics);
	}
}

export function parseMollieWebhookBody(
	body: Uint8Array,
	contentType?: string,
): MollieWebhookEnvelope {
	const diagnostics = classifyMollieWebhookBody(body, contentType);
	assertMollieWebhookBodySize(body, diagnostics);
	const text = decodeBody(body).trim();
	if (diagnostics.bodyKind === "json") {
		let parsed: unknown;
		try {
			parsed = JSON.parse(text);
		} catch {
			throw envelopeError("Mollie webhook envelope is invalid.", diagnostics);
		}
		if (
			isRecord(parsed) &&
			typeof parsed.id === "string" &&
			typeof parsed.entityId === "string"
		) {
			const createdAt =
				typeof parsed.createdAt === "string" ? parsed.createdAt : undefined;
			assertMollieWebhookCreatedAt(createdAt, diagnostics);
			return {
				kind: "next_gen",
				id: parsed.id,
				type: typeof parsed.type === "string" ? parsed.type : "unknown",
				resource:
					typeof parsed.resource === "string" ? parsed.resource : "event",
				entityId: parsed.entityId,
				createdAt,
			};
		}
		throw envelopeError("Mollie webhook envelope is invalid.", diagnostics);
	}
	const params = new URLSearchParams(text);
	const id = params.get("id");
	if (!id) {
		throw envelopeError("Mollie webhook envelope is invalid.", diagnostics);
	}
	return { kind: "classic", id };
}
